import LogoSkrill from "app/components/Logos/LogoSkrill";
import { useState } from "react";
import type { IntlShape } from "react-intl";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import * as skrillAPI from "../../../../api/skrillApi";
import { TextInput } from "../../../../components/FormInputs/FormInputs";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import Modal from "../../../../components/Modal/Modal";
import type { UserType } from "../../../../types/userType";
import ManageFundingDepositDetails from "../../components/ManageFundingDepositDetails/ManageFundingDepositDetails";
import ManageFundingLogoHeader from "../../components/ManageFundingLogoHeader/ManageFundingLogoHeader";
import FormSubmittedScreen from "../FormSubmittedScreen/FormSubmittedScreen";
import "./SkrillFundingScreenDesktop.scss";
import "./SkrillFundingScreenMobile.scss";

export const TabTypes = {
  DEPOSIT: "DEPOSIT",
  WITHDRAW: "WITHDRAW",
};

function SkrillFundingScreen(props: { user: UserType; intl: IntlShape }) {
  const history = useHistory();
  const [form, setForm] = useState();
  const [tab, setTabValue] = useState(TabTypes.DEPOSIT);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [otherError, setOtherError] = useState("");
  const [iFrameUrl, setIFrameUrl] = useState("");
  const [iFrameActive, setIFrameActive] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user, intl } = props;

  const deposit = () => {
    skrillAPI
      .depositFunds(amount, email)
      .then(resp => {
        setIFrameUrl(resp.data.iframe.url);
        setIFrameActive(true);
        setFormSubmitted(false);
      })
      .catch(error => {
        const errorData = error.response.data.errors;
        handleErrors(errorData);
        setFormSubmitted(false);
      });
  };

  const withdraw = () => {
    skrillAPI
      .withdrawFunds(amount, email)
      .then(resp => {
        setFormSubmitted(false);
        setSuccess(true);
      })
      .catch(error => {
        let errorData = null;

        if (error.response) {
          errorData = error.response.data.errors;
          handleErrors(errorData);
        }
        setFormSubmitted(false);
      });
  };

  const closeModal = () => setIFrameActive(false);

  const handleChange = (id: string, value: string) => {
    if (id === "email") {
      setEmail(value);
    }
    if (id === "amount") {
      setAmount(value);
    }
  };

  const handleErrors = errorData => {
    let amountError = "";
    let emailError = "";
    let otherError = "";

    if (errorData.length > 0) {
      errorData.forEach(error => {
        if (error.messages) {
          if (error.field === "emailId") {
            emailError = error.messages[0];
          } else if (error.field === "amount") {
            amountError = error.messages[0];
          } else {
            otherError = error.messages[0];
          }
        } else if (error.detail) {
          otherError = error.detail;
        }
      });
    }
    setEmailError(emailError);
    setAmountError(amountError);
    setOtherError(otherError);
  };

  const newTransaction = () => {
    setFormSubmitted(false);
    setSuccess(false);
  };

  const setTab = (tabType: string) => {
    if (tab === tabType) {
      return null;
    }
    setTabValue(tabType);
  };

  const getTabs = () => {
    return [
      {
        title: intl.formatMessage({
          id: "cashier-deposit",
        }),
        callback: () => setTab(TabTypes.DEPOSIT),
        isActive: tab === TabTypes.DEPOSIT,
        id: "cashier-deposit",
      },
      {
        title: intl.formatMessage({
          id: "cashier-withdraw",
        }),
        callback: () => setTab(TabTypes.WITHDRAW),
        isActive: tab === TabTypes.WITHDRAW,
        id: "cashier-withdraw",
      },
    ];
  };

  const onSubmit = e => {
    e.preventDefault();
    setFormSubmitted(true);

    if (tab === TabTypes.DEPOSIT) {
      deposit();
    } else if (tab === TabTypes.WITHDRAW) {
      withdraw();
    }
  };

  const I18n = defineMessages({
    email: {
      id: "cashier-skrillemailid",
    },
    amount: {
      id: "cashier-amount",
    },
  });

  // If form successfully submitted show FormSubmittedScreen
  return !success ? (
    <div className="skrill-funding">
      <ManageFundingLogoHeader
        history={history}
        getTabs={getTabs()}
        logo={<LogoSkrill />}
      />
      <Modal
        modalOpen={iFrameActive}
        closeModal={closeModal}
        contentLabel="Skrill"
        cancelButton
        cancelButtonText="Close"
        className="skrill-modal"
      >
        <div className="skrill-funding__iframe">
          <iframe
            title="Skrill Authentication"
            src={iFrameUrl}
            frameBorder="0"
          />
        </div>
      </Modal>
      <div className="skrill-funding__content">
        {tab === TabTypes.DEPOSIT && (
          <ManageFundingDepositDetails user={user} />
        )}
        <form
          onSubmit={onSubmit}
          ref={(ref: any) => {
            setForm(ref);
          }}
        >
          <TextInput
            type="email"
            id="email"
            label={I18n.email}
            onChange={handleChange}
          />
          <div className="error-msg">{emailError}</div>
          <TextInput
            type="text"
            id="amount"
            label={I18n.amount}
            onChange={handleChange}
          />
          <div className="error-msg">{amountError}</div>
          {formSubmitted ? (
            <LoadingSpinner />
          ) : (
            <button id="deposit__button" type="submit" disabled={formSubmitted}>
              {tab === TabTypes.DEPOSIT && (
                <FormattedMessage tagName="span" id="cashier-deposit-button" />
              )}
              {tab === TabTypes.WITHDRAW && (
                <FormattedMessage tagName="span" id="cashier-withdraw-button" />
              )}
            </button>
          )}
          <div className="error-msg">{otherError}</div>
        </form>
        <div className="skrill-funding__content__links">
          <a href="http://www.skrill.com" target="_new">
            <FormattedMessage tagName="span" id="cashier-createaccount" />
          </a>
        </div>
      </div>
    </div>
  ) : (
    // skrill deposit tracking not working, will
    // circle back when we have time, creating a backlog ticket for this
    <FormSubmittedScreen
      user={user}
      verified={user["verifiedStatus"]}
      selectedTab={tab}
      newTransaction={history.goBack}
    />
  );
}

export default injectIntl(SkrillFundingScreen);
